import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { FiredAlertPayload, AlertQueueItem } from '@roll1/shared';

const DEFAULT_DURATION_MS = 5000;
const MAX_STALE_MS = 30000; // Skip alerts older than 30 seconds

export function useAlertQueue() {
  const [queue, setQueue] = useState<AlertQueueItem[]>([]);
  const [currentAlert, setCurrentAlert] = useState<FiredAlertPayload | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentAlertRef = useRef<FiredAlertPayload | null>(null);

  const dequeue = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setCurrentAlert(null);
    currentAlertRef.current = null;
  }, []);

  const processNext = useCallback(() => {
    setQueue(prev => {
      if (prev.length === 0) return prev;

      // Find first non-stale alert
      const now = Date.now();
      let nextIdx = -1;
      for (let i = 0; i < prev.length; i++) {
        if (now - prev[i].receivedAt < MAX_STALE_MS) {
          nextIdx = i;
          break;
        }
      }

      if (nextIdx === -1) {
        // All stale, clear the queue
        return [];
      }

      const next = prev[nextIdx];
      const remaining = prev.slice(nextIdx + 1);

      setCurrentAlert(next.alert);
      currentAlertRef.current = next.alert;

      const duration = next.alert.durationMs > 0 ? next.alert.durationMs : DEFAULT_DURATION_MS;
      const expectedAlert = next.alert;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (currentAlertRef.current === expectedAlert) {
          setCurrentAlert(null);
          currentAlertRef.current = null;
        }
      }, duration);

      return remaining;
    });
  }, []);

  const enqueue = useCallback((alert: FiredAlertPayload) => {
    const item: AlertQueueItem = {
      alert,
      receivedAt: Date.now(),
    };

    if (!currentAlertRef.current) {
      // No current alert — show immediately
      setCurrentAlert(alert);
      currentAlertRef.current = alert;

      const duration = alert.durationMs > 0 ? alert.durationMs : DEFAULT_DURATION_MS;
      const expectedAlert = alert;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (currentAlertRef.current === expectedAlert) {
          setCurrentAlert(null);
          currentAlertRef.current = null;
        }
      }, duration);
    } else {
      setQueue(prev => [...prev, item]);
    }
  }, []);

  // When current alert ends, process next in queue
  useEffect(() => {
    if (!currentAlert && queue.length > 0) {
      processNext();
    }
  }, [currentAlert, queue.length, processNext]);

  // Handle page visibility — skip stale alerts when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !currentAlertRef.current) {
        processNext();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [processNext]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return useMemo(() => ({
    currentAlert,
    enqueue,
    dequeue,
    queueLength: queue.length,
  }), [currentAlert, enqueue, dequeue, queue.length]);
}
